import dynamic from 'next/dynamic';
import SceneLoadError from './scene-load-error';
import SceneSkeleton from './scene-skeleton';
import type { SceneProps } from './types';
import type { ComponentType } from 'react';

// Scenes are code-split (DEV-6485). The stage only renders a scene's
// `content` once that scene is active, so the chunk is fetched on open
// rather than shipped in `_app` to every visitor. `SceneSkeleton` fills the
// gap so the surface keeps its size while the chunk lands, and `ssr: false`
// is safe because the stage is always closed on the server. A chunk that
// errors or times out is retried once; if that fails too the skeleton is
// swapped for a refresh prompt rather than left up forever (DEV-7273).
export const SCENE_LOAD_TIMEOUT_MS = 10_000;
type SceneLoader = () => Promise<{
  default: ComponentType<SceneProps>;
}>;
const withTimeout = (loader: SceneLoader): SceneLoader => () => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error('Scene chunk load timed out')), SCENE_LOAD_TIMEOUT_MS);
  loader().then(resolve, reject).finally(() => clearTimeout(timer));
});
export const lazyScene = (loader: SceneLoader) => {
  const attempt = withTimeout(loader);
  return dynamic(() => attempt().catch(attempt), {
    ssr: false,
    loading: ({
      error
    }) => error ? <SceneLoadError /> : <SceneSkeleton />
  });
};